import { Link, type LinkProps } from '@tanstack/react-router'
import { ArrowRight, Info } from 'lucide-react'

import { PageHeader } from '@/components/page-header'
import { PermissionGate } from '@/components/permission-gate'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { REPORTS_CAPABILITY_GAPS } from '@/features/reports/constants'
import { RegulatoryExtractsSection } from '@/features/reports/regulatory-extracts'
import { REPORTS_MODULE_PERMISSIONS, REPORTS_PERMISSIONS, useReportsPermissions } from '@/features/reports/permissions'
import type { PermissionCode } from '@/lib/permissions'

interface ReportCardDefinition {
  title: string
  description: string
  to: LinkProps['to']
  /** Omitted for a card open to anyone who reaches the module at all — the snapshot browser. */
  permission?: PermissionCode
}

const BUILT_REPORTS: ReportCardDefinition[] = [
  {
    title: 'Portfolio at risk & arrears ageing',
    description: 'Every facility behind, worst first, and the same finding rolled up by branch, officer or group.',
    to: '/reports/par',
    permission: REPORTS_PERMISSIONS.parView,
  },
  {
    title: 'Collections register',
    description: 'Repayments collected in a range, by value date, against what was expected.',
    to: '/reports/collections',
    permission: REPORTS_PERMISSIONS.collectionsView,
  },
  {
    title: 'Disbursements register',
    description: 'Rounds disbursed in a range, with what actually paid out net of fees and any deposit.',
    to: '/reports/disbursements',
    permission: REPORTS_PERMISSIONS.portfolioView,
  },
  {
    title: 'Officer, branch & group performance',
    description: 'Portfolio quality and collection activity, sliced by whichever dimension is asked for.',
    to: '/reports/performance',
    permission: REPORTS_PERMISSIONS.officerPerformanceView,
  },
  {
    title: 'Group health',
    description: 'Every group’s standing and portfolio quality as at a date, qualifying or not.',
    to: '/reports/group-health',
    permission: REPORTS_PERMISSIONS.groupHealthView,
  },
  {
    title: 'Trial balance',
    description: 'The general ledger’s own position, for a branch or the institution, as at a business date.',
    to: '/reports/trial-balance',
    permission: REPORTS_PERMISSIONS.trialBalanceView,
  },
  {
    title: 'Balance sheet',
    description: 'Assets against liabilities and equity as at a date, and whether the two sides agree.',
    to: '/reports/balance-sheet',
    permission: REPORTS_PERMISSIONS.trialBalanceView,
  },
  {
    title: 'Income statement',
    description: 'Income against expense over a period — the profit-and-loss view of the ledger’s own movement.',
    to: '/reports/income-statement',
    permission: REPORTS_PERMISSIONS.trialBalanceView,
  },
  {
    title: 'Report snapshots',
    description: 'Freeze a report as at a date so its figures cannot move, and browse what has already been frozen.',
    to: '/reports/snapshots',
  },
]

/**
 * The reporting module's landing page.
 *
 * A menu rather than a tabbed hub — see `docs/mobile-policy.md`'s general split and the ledger's
 * own note on why *that* module is tabs: each of these screens carries substantial filter state of
 * its own, and each is worth a URL an operator can bookmark or send to a colleague, rather than a
 * tab position that resets on reload.
 *
 * What the module still cannot do — a real gap in the API or in this client, as opposed to a screen
 * simply not existing yet — is stated below the menu rather than left for a reader to discover from
 * an absence; see {@link ReportsCapabilityGaps}.
 */
export function ReportsPage() {
  const permissions = useReportsPermissions()

  if (!permissions.canEnterModule) {
    return (
      <>
        <PageHeader
          title="Reports"
          description="Operational enquiries, portfolio analytics, and the extracts behind the regulatory returns."
        />
        <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
          <p className="text-sm font-medium text-heading">Reporting is restricted</p>
          <p className="max-w-lg text-xs text-muted">
            Nothing in this module is open to your role. Its screens are gated on the reporting
            permissions —{' '}
            {REPORTS_MODULE_PERMISSIONS.map((code, index) => (
              <span key={code}>
                {index > 0 ? ', ' : ''}
                <span className="font-mono">{code}</span>
              </span>
            ))}{' '}
            — and the server checks each of them again on every request.
          </p>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Reports"
        description="Analytical reads over the loan and collection books — never the transaction tables a teller or a collection sheet is posting against."
      />

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {BUILT_REPORTS.map((report) => (
          <PermissionGate key={report.to} permission={report.permission}>
            <Link to={report.to} className="block">
              <Card className="flex h-full flex-col gap-1.5 p-4 transition-colors hover:border-border-strong hover:bg-surface-hover">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-heading">{report.title}</p>
                  <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
                </div>
                <p className="text-xs text-body">{report.description}</p>
              </Card>
            </Link>
          </PermissionGate>
        ))}
      </div>

      <RegulatoryExtractsSection />

      <ReportsCapabilityGaps />
    </>
  )
}

/**
 * What the reporting module is catalogued to do and cannot yet — the module's own version of
 * `LedgerCapabilityGaps`. A user who cannot find a control here should learn whether it is missing
 * from the API, missing from this client, or missing from their own access, and never be shown a
 * plausible-looking empty screen in place of a capability that does not exist.
 */
function ReportsCapabilityGaps() {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-heading">What this module still cannot do</h2>
      <div className="space-y-2">
        {REPORTS_CAPABILITY_GAPS.map((gap) => (
          <Alert key={gap.title} tone="info">
            <Info aria-hidden />
            <div className="min-w-0 space-y-1">
              <AlertTitle className="flex flex-wrap items-center gap-2">
                {gap.title}
                <Badge tone="neutral" className="font-mono">
                  {gap.permission}
                </Badge>
              </AlertTitle>
              <AlertDescription>{gap.detail}</AlertDescription>
            </div>
          </Alert>
        ))}
      </div>
    </section>
  )
}
